/**
 * =============================================================================
 *
 * @format
 */

/*===============================  IMPORTS  ===================================*/
/*=============================================================================*/
import React, { useEffect, useState } from "react";
import Customize_Reworked from "./index";
import { useDispatch, useSelector } from "react-redux";
import { description, title } from "../../constants/tooltipConstants";
import { customizeProduct } from "../../actions/blindActions";
import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Tooltip } from "./Tooltip";

const OptionCard = ({
	selected,
	displayCountryPrice,
	onClick,
	image,
	title,
	tooltip,
	price = 0,
	children,
	showPrice = true,
}) => (
	<motion.div
		whileHover={{ scale: 1.02 }}
		whileTap={{ scale: 0.98 }}
		className={`
		relative p-4 border-2 rounded-2xl cursor-pointer transition-all
		${
			selected
				? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
				: "border-gray-200 hover:border-primary/30"
		}
	  `}
		onClick={onClick}
	>
		{/* Image */}
		<div className="rounded-lg mb-4">
			<ImageZoomWithButton
				item={{
					image: image,
					colour: title,
				}}
				isFabricSelected={selected}
				customize={true}
			/>
		</div>

		{/* Content */}
		<div className="space-y-4">
			<div className="flex flex-col items-start justify-between">
				<div className="flex items-center gap-2">
					<input
						aria-label={title}
						type="radio"
						checked={selected}
						onChange={() => {}}
						className="w-4 h-4 text-primary"
					/>
					<span className="font-medium">{title}</span>
				</div>
				{showPrice && (
					<div className="flex items-center justify-between w-full">
						<span className="text-primary font-medium">
							{price === 0 ? "Included" : `+${displayCountryPrice(price)}`}
						</span>
						{tooltip && tooltip.content && (
							<Tooltip title={tooltip.title} content={tooltip.content}>
								<button className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100">
									?
								</button>
							</Tooltip>
						)}
					</div>
				)}
			</div>
			{children}
		</div>
	</motion.div>
);

const Customize_Valance = () => {
	/*=============================================================================*/
	/*===============================  VARIABLES  =================================*/
	/*=============================================================================*/
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) => state.customize.customizeProduct
	);
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(
		productInfo.valanceArray ? productInfo.valanceArray : []
	);
	const country = useSelector((state) => state.country);
	const [valanceColor, setValanceColor] = useState(productInfo.Valance_Color);
	const [lChannel, setLChannel] = useState(productInfo.L_Channel);
	const [selectedRollType, setSelectedRollType] = useState(
		productInfo.Roll_Type
	);

	const shade = productInfo.Type.toLowerCase() + " shade";
	var valanceArray = productInfo.valanceArray;
	var [valance, setValance] = useState(productInfo.Valance);
	var [channelPrice, setChannelPrice] = useState([0, 0]);
	/*=============================================================================*/
	/*===============================  USE EFFECT  ================================*/
	/*=============================================================================*/
	useEffect(() => {
		loadValance();
	}, [valanceArray]);

	useEffect(() => {
		if (valanceArray != null) {
			let temp = [];
			temp.push(valanceArray[0].LChannel);
			temp.push(valanceArray[0].LBChannel);

			setChannelPrice(temp);
			setLoading(false);
		}
	}, [
		valanceArray,
		valance,
		valanceColor,
		productInfo.Operation,
		productInfo.Valance,
		productInfo.L_Channel,
		selectedRollType,
	]);

	useEffect(() => {
		if (loading) return;
		dispatchDetails();
	}, [valanceColor, lChannel, selectedRollType, valance]);

	/*=============================================================================*/
	/*===============================  FUNCTIONS  =================================*/
	/*=============================================================================*/
	const dispatchDetails = () => {
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					Valance: valance,
					Valance_Color: valanceColor,
					L_Channel: lChannel,
					Roll_Type: selectedRollType,
					Program: "Customized " + productInfo.Type + " Shade",
					series: productInfo.SG_Color,
				},
				optionInfo: {
					...optionInfo,
					// OUR_PRICE: +totalPrice.toString().split("\n")[0],
				},
			})
		);
	};

	const handleValance = (id) => {
		if (id === "Open Roll") setValanceColor("-");

		setValance(id);
		// Set to first color if currentcolor doesn't exist in new valance
		const colours = valanceArray.find((index) => index.valance == id).color;
		if (!colours.includes(valanceColor)) setValanceColor(colours[0]);
	};

	const isSelected = (product) => {
		return product.toLowerCase() === valance.toLowerCase();
	};

	const displayCountryPrice = (price) => {
		price = parseFloat(price);
		const discount1 = parseFloat(productInfo?.Discount) || 0;

		// Extra discount sets here, if you want to enable it, set extraDiscount > 0
		// For this event, we set a global 15% AFTER the first discount applies
		const discount2 = parseFloat(productInfo?.Global_Discount) || 0; // Have to set to 0.15 for default, otherwise customized product (does not belong to any "program") will not have discount

		if (country == "USA") {
			return `$${(
				price *
				1.5 *
				(1 - discount1) *
				(1 - discount2) *
				0.75
			).toFixed(2)} USD`;
		} else {
			return `$${(price * 1.5 * (1 - discount1) * (1 - discount2)).toFixed(
				2
			)} CAD`;
		}
	};

	const loadValance = () => {
		if (valanceArray != null) {
			// Check if valance in valanceArray has valance, if not then set it to first index
			if (!valanceArray.some((index) => index.valance === valance)) {
				setValance(valanceArray[0].valance);
				if (valanceArray[0] === "Open Roll") {
					setValanceColor("-");
				} else {
					setValanceColor(valanceArray[0].color[0]);
				}
			}
			setData(valanceArray);
			setLoading(false);
		} else {
			alert("Please select a control first.");
			window.location.href = "/customize/control";
		}
	};

	function getValanceTypeDisplayName(valanceName) {
		switch (valanceName) {
			case "Zero Gravity":
				return "Glide Chainless";
			case 'SB 3" Fascia':
				return '3" Fascia';
			case "SB Compact Curved Cassette":
				return "Compact Curved Cassette";
			case 'SB 4" Fascia':
				return '4" Fascia';
			case "SB Curved Cassette":
				return "Curved Cassette";
			default:
				return valanceName;
		}
	}

	/*=============================================================================*/
	/*===============================  RENDER  ====================================*/
	/*=============================================================================*/
	return (
		<Customize_Reworked>
			<div className="space-y-8">
				{loading ? (
					<div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
						<div className="flex flex-col items-center gap-4">
							<div
								className="w-32 h-32 bg-contain bg-center bg-no-repeat"
								style={{ backgroundImage: "url(/images/logoDark.png)" }}
							></div>
							<p className="text-lg font-medium">Loading...</p>
						</div>
					</div>
				) : (
					<>
						{/* Valance Selection */}
						<div className="space-y-6">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
									<Crown className="w-6 h-6 text-primary" />
								</div>
								<h3 className="text-lg font-medium">Select Valance Style</h3>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								{data.map((item) => (
									<OptionCard
										displayCountryPrice={displayCountryPrice}
										key={item.valance}
										selected={isSelected(item.valance.toLowerCase())}
										onClick={() => handleValance(item.valance)}
										image={item.img}
										title={getValanceTypeDisplayName(item.valance)}
										tooltip={{
											title: title[item.valance],
											content: description[item.valance],
										}}
										price={item.price}
									>
										<div className="space-y-4 pt-4 border-t">
											{item.valance !== "Open Roll" && (
												<>
													<div className="space-y-2">
														<label
															className="block text-sm font-medium"
															for={item.valance}
														>
															Valance Colour
														</label>
														<select
															id={item.valance}
															aria-label={`${item.valance}-Colours`}
															value={
																isSelected(item.valance.toLowerCase())
																	? valanceColor
																	: ""
															}
															onChange={(e) => setValanceColor(e.target.value)}
															className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
														>
															<option value="">--Select--</option>
															{item.color !== "-" ? (
																item.color.map((colour) => (
																	<option
																		key={colour}
																		value={colour}
																		className={`
									  ${colour === "Black" ? "text-white bg-[#000]" : ""}
									  ${colour === "Brown" ? "text-white bg-[#665339]" : ""}
									  ${colour === "Anodized" ? "bg-[#d3d2ce]" : ""}
									`}
																	>
																		{colour}
																	</option>
																))
															) : (
																<option value="-">-</option>
															)}
														</select>
													</div>
												</>
											)}

											{item.roll_supported && (
												<div className="space-y-2">
													<label
														for="rollType"
														className="block text-sm font-medium"
													>
														Roll Type
													</label>
													<select
														id="rollType"
														arial-label="Roll Type"
														value={
															isSelected(item.valance.toLowerCase())
																? selectedRollType
																: ""
														}
														onChange={(e) =>
															setSelectedRollType(e.target.value)
														}
														className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
													>
														<option value="Regular">Regular</option>
														<option value="Reverse">Reverse</option>
													</select>
												</div>
											)}
										</div>
									</OptionCard>
								))}
							</div>
						</div>

						{/* Light Guards Section */}
						{/* {shade === "roller shade" && (
							<div className="space-y-6">
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
										<Shield className="w-6 h-6 text-primary" />
									</div>
									<h3 className="text-lg font-medium">
										Select Light Guard Options
									</h3>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
									<OptionCard
										displayCountryPrice={displayCountryPrice}
										selected={lChannel === "None"}
										onClick={() => setLChannel("None")}
										image="/images/diagram/lc/none.png"
										title="None"
										showPrice={false}
									/>

									<OptionCard
										displayCountryPrice={displayCountryPrice}
										selected={lChannel === "L Channels"}
										onClick={() => setLChannel("L Channels")}
										image="/images/diagram/lc/l_side_channels.png"
										title="Light Guards"
										tooltip={{
											title: title["L Channels"],
											content: description["L Channels"],
										}}
										price={channelPrice[0]}
									/>

									<OptionCard
										displayCountryPrice={displayCountryPrice}
										selected={lChannel === "L Channels & Bottom Channels"}
										onClick={() => setLChannel("L Channels & Bottom Channels")}
										image="/images/diagram/lc/l_side_bottom_channels.png"
										title="Light Guards & Bottom Light Guards"
										tooltip={{
											title: title["L Channels & Bottom Channels"],
											content: description["L Channels & Bottom Channels"],
										}}
										price={channelPrice[1]}
									/>
								</div>
							</div>
						)} */}
					</>
				)}
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Valance;
